import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  ArrowBack as BackIcon,
  Delete as DeleteIcon,
  Facebook as FacebookIcon,
  Instagram as InstagramIcon,
  Pause as PauseIcon,
  PlayArrow as PlayIcon,
  Refresh as RefreshIcon,
  Settings as SettingsIcon,
  Visibility,
  AdsClick,
  AttachMoney,
  People,
} from '@mui/icons-material';
import metaService, {
  CreateAdPayload,
  CreateAdSetPayload,
  CreateCampaignPayload,
  MetaAd,
  MetaAdSet,
  MetaCampaign,
  MetaCampaignObjective,
  MetaConfig,
  MetaInstagramPosition,
  MetaOptimizationGoal,
} from '../../services/marketing-meta.service';
import CapiPanel from './CapiPanel';
import { PageHeader, StatCard, EmptyState } from '../../components/Marketing';

// ─── Constants ────────────────────────────────────────────────────────────────

const OBJECTIVES: { value: MetaCampaignObjective; label: string; goal: MetaOptimizationGoal }[] = [
  { value: 'OUTCOME_AWARENESS', label: 'Notoriedade', goal: 'REACH' },
  { value: 'OUTCOME_TRAFFIC', label: 'Tráfego', goal: 'LINK_CLICKS' },
  { value: 'OUTCOME_ENGAGEMENT', label: 'Envolvimento', goal: 'POST_ENGAGEMENT' },
  { value: 'OUTCOME_LEADS', label: 'Leads', goal: 'LEAD_GENERATION' },
  { value: 'OUTCOME_SALES', label: 'Vendas', goal: 'OFFSITE_CONVERSIONS' },
];

const IG_POSITIONS: { value: MetaInstagramPosition; label: string }[] = [
  { value: 'stream', label: 'Feed' },
  { value: 'story', label: 'Stories' },
  { value: 'reels', label: 'Reels' },
  { value: 'explore', label: 'Explorar' },
];

const CTA_OPTIONS = [
  'LEARN_MORE', 'SHOP_NOW', 'SIGN_UP', 'CONTACT_US', 'DOWNLOAD', 'GET_OFFER',
];

const PERIOD_OPTIONS = [
  { value: 'last_7d', label: 'Últimos 7 dias' },
  { value: 'last_30d', label: 'Últimos 30 dias' },
  { value: 'last_90d', label: 'Últimos 90 dias' },
];

const STATUS_COLORS: Record<string, 'default' | 'success' | 'warning' | 'error'> = {
  ACTIVE: 'success', PAUSED: 'warning', DELETED: 'error', ARCHIVED: 'default',
};

const WIZARD_STEPS = ['Campanha', 'Ad Set (Instagram)', 'Anúncio'];

// ─── Default state ────────────────────────────────────────────────────────────

const defaultCampaignForm: CreateCampaignPayload = {
  name: '', objective: 'OUTCOME_AWARENESS', daily_budget: 1000, status: 'PAUSED',
};

const defaultAdSetForm: Omit<CreateAdSetPayload, 'campaign_id'> = {
  name: '',
  optimization_goal: 'REACH',
  daily_budget: 500,
  status: 'PAUSED',
  targeting: {
    geo_locations: { countries: ['CV'] },
    age_min: 18,
    age_max: 65,
    publisher_platforms: ['instagram'],
    instagram_positions: ['stream', 'story', 'reels'],
  },
};

const defaultAdForm: Omit<CreateAdPayload, 'ad_set_id'> = {
  name: '', message: '', link: 'https://pagali.cv', image_url: '',
  headline: '', call_to_action: 'LEARN_MORE', status: 'PAUSED',
};

// ─── Component ────────────────────────────────────────────────────────────────

export default function MetaAds() {
  const [tab, setTab] = useState(0);
  const [preset, setPreset] = useState('last_30d');
  const [config, setConfig] = useState<MetaConfig | null>(null);
  const [summary, setSummary] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [notConfigured, setNotConfigured] = useState(false);

  // Campaigns
  const [campaigns, setCampaigns] = useState<MetaCampaign[]>([]);
  const [selectedCampaign, setSelectedCampaign] = useState<MetaCampaign | null>(null);

  // Ad Sets
  const [adSets, setAdSets] = useState<MetaAdSet[]>([]);
  const [adSetsLoading, setAdSetsLoading] = useState(false);
  const [selectedAdSet, setSelectedAdSet] = useState<MetaAdSet | null>(null);

  // Ads
  const [ads, setAds] = useState<MetaAd[]>([]);
  const [adsLoading, setAdsLoading] = useState(false);

  // Wizard
  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardStep, setWizardStep] = useState(0);
  const [campaignForm, setCampaignForm] = useState<CreateCampaignPayload>(defaultCampaignForm);
  const [adSetForm, setAdSetForm] = useState<Omit<CreateAdSetPayload, 'campaign_id'>>(defaultAdSetForm);
  const [adForm, setAdForm] = useState<Omit<CreateAdPayload, 'ad_set_id'>>(defaultAdForm);
  const [createdCampaignId, setCreatedCampaignId] = useState<string | null>(null);
  const [createdAdSetId, setCreatedAdSetId] = useState<string | null>(null);
  const [wizardSaving, setWizardSaving] = useState(false);

  // Config dialog
  const [configOpen, setConfigOpen] = useState(false);
  const [configForm, setConfigForm] = useState({ access_token: '', ad_account_id: '', page_id: '', ig_user_id: '' });
  const [configSaving, setConfigSaving] = useState(false);

  const [snack, setSnack] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false, message: '', severity: 'success',
  });

  const showSnack = (message: string, severity: 'success' | 'error' = 'success') =>
    setSnack({ open: true, message, severity });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cfg, sum, camps] = await Promise.all([
        metaService.getConfig(),
        metaService.getSummary(preset),
        metaService.getCampaigns(preset, 25),
      ]);
      setConfig(cfg);
      setSummary(sum);
      setCampaigns(camps.data);
      setNotConfigured(!!camps.not_configured);
      setConfigForm({
        access_token: '',
        ad_account_id: cfg.ad_account_id || '',
        page_id: cfg.page_id || '',
        ig_user_id: cfg.ig_user_id || '',
      });
    } catch {
      showSnack('Erro ao carregar dados Meta Ads', 'error');
    } finally {
      setLoading(false);
    }
  }, [preset]);

  useEffect(() => { load(); }, [load]);

  const loadAdSets = useCallback(async (campaign: MetaCampaign) => {
    setAdSetsLoading(true);
    setAdSets([]);
    try {
      const data = await metaService.getAdSets(campaign.id, preset);
      setAdSets(data);
    } catch {
      showSnack('Erro ao carregar Ad Sets', 'error');
    } finally {
      setAdSetsLoading(false);
    }
  }, [preset]);

  const loadAds = useCallback(async (adSet: MetaAdSet) => {
    setAdsLoading(true);
    setAds([]);
    try {
      const data = await metaService.getAds(adSet.id, preset);
      setAds(data);
    } catch {
      showSnack('Erro ao carregar Anúncios', 'error');
    } finally {
      setAdsLoading(false);
    }
  }, [preset]);

  const handleSelectCampaign = (c: MetaCampaign) => {
    setSelectedCampaign(c);
    setSelectedAdSet(null);
    setAdSets([]);
    setAds([]);
    setTab(1);
    loadAdSets(c);
  };

  const handleSelectAdSet = (as: MetaAdSet) => {
    setSelectedAdSet(as);
    setAds([]);
    setTab(2);
    loadAds(as);
  };

  // ─── Wizard ────────────────────────────────────────────────────────────────

  const openWizard = () => {
    setWizardStep(0);
    setCampaignForm(defaultCampaignForm);
    setAdSetForm(defaultAdSetForm);
    setAdForm(defaultAdForm);
    setCreatedCampaignId(null);
    setCreatedAdSetId(null);
    setWizardOpen(true);
  };

  const handleWizardNext = async () => {
    setWizardSaving(true);
    try {
      if (wizardStep === 0) {
        const { id } = await metaService.createCampaign(campaignForm);
        setCreatedCampaignId(id);
        // Pre-fill ad set optimization_goal from objective
        const obj = OBJECTIVES.find(o => o.value === campaignForm.objective);
        if (obj) setAdSetForm(f => ({ ...f, optimization_goal: obj.goal, name: `Ad Set — ${campaignForm.name}` }));
        setWizardStep(1);
      } else if (wizardStep === 1) {
        const { id } = await metaService.createAdSet({ ...adSetForm, campaign_id: createdCampaignId! });
        setCreatedAdSetId(id);
        setAdForm(f => ({ ...f, name: `Anúncio — ${campaignForm.name}` }));
        setWizardStep(2);
      } else if (wizardStep === 2) {
        await metaService.createAd({ ...adForm, ad_set_id: createdAdSetId! });
        showSnack('Campanha Instagram criada com sucesso!');
        setWizardOpen(false);
        load();
      }
    } catch (e: any) {
      showSnack(e?.response?.data?.error || e.message || 'Erro no passo ' + (wizardStep + 1), 'error');
    } finally {
      setWizardSaving(false);
    }
  };

  const handleWizardBack = () => setWizardStep(s => s - 1);

  // ─── Toggle status helpers ─────────────────────────────────────────────────

  const toggleCampaign = async (c: MetaCampaign) => {
    const next = c.status === 'ACTIVE' ? 'PAUSED' : 'ACTIVE';
    try {
      await metaService.updateCampaignStatus(c.id, next);
      setCampaigns(prev => prev.map(x => x.id === c.id ? { ...x, status: next } : x));
    } catch (e: any) { showSnack(e?.response?.data?.error || 'Erro', 'error'); }
  };

  const deleteCampaign = async (id: string) => {
    if (!window.confirm('Eliminar esta campanha no Meta?')) return;
    try {
      await metaService.deleteCampaign(id);
      setCampaigns(prev => prev.filter(x => x.id !== id));
      if (selectedCampaign?.id === id) { setSelectedCampaign(null); setAdSets([]); }
    } catch (e: any) { showSnack(e?.response?.data?.error || 'Erro', 'error'); }
  };

  const toggleAdSet = async (as: MetaAdSet) => {
    const next = as.status === 'ACTIVE' ? 'PAUSED' : 'ACTIVE';
    try {
      await metaService.updateAdSetStatus(as.id, next);
      setAdSets(prev => prev.map(x => x.id === as.id ? { ...x, status: next } : x));
    } catch (e: any) { showSnack(e?.response?.data?.error || 'Erro', 'error'); }
  };

  const deleteAdSet = async (id: string) => {
    if (!window.confirm('Eliminar este Ad Set?')) return;
    try {
      await metaService.deleteAdSet(id);
      setAdSets(prev => prev.filter(x => x.id !== id));
      if (selectedAdSet?.id === id) { setSelectedAdSet(null); setAds([]); }
    } catch (e: any) { showSnack(e?.response?.data?.error || 'Erro', 'error'); }
  };

  const toggleAd = async (ad: MetaAd) => {
    const next = ad.status === 'ACTIVE' ? 'PAUSED' : 'ACTIVE';
    try {
      await metaService.updateAdStatus(ad.id, next);
      setAds(prev => prev.map(x => x.id === ad.id ? { ...x, status: next } : x));
    } catch (e: any) { showSnack(e?.response?.data?.error || 'Erro', 'error'); }
  };

  const deleteAd = async (id: string) => {
    if (!window.confirm('Eliminar este anúncio?')) return;
    try {
      await metaService.deleteAd(id);
      setAds(prev => prev.filter(x => x.id !== id));
    } catch (e: any) { showSnack(e?.response?.data?.error || 'Erro', 'error'); }
  };

  const handleSaveConfig = async () => {
    setConfigSaving(true);
    try {
      const payload: any = { ad_account_id: configForm.ad_account_id, page_id: configForm.page_id, ig_user_id: configForm.ig_user_id };
      if (configForm.access_token) payload.access_token = configForm.access_token;
      await metaService.saveConfig(payload);
      showSnack('Configuração guardada');
      setConfigOpen(false);
      load();
    } catch { showSnack('Erro ao guardar configuração', 'error'); }
    finally { setConfigSaving(false); }
  };

  const fmt = (n: number) => n?.toLocaleString('pt-CV') ?? '—';
  const fmtMoney = (n: number) => n != null ? `${n.toFixed(2)} CVE` : '—';

  const toggleIgPosition = (pos: MetaInstagramPosition) => {
    const positions = adSetForm.targeting.instagram_positions || [];
    const next = positions.includes(pos) ? positions.filter(p => p !== pos) : [...positions, pos];
    setAdSetForm(f => ({ ...f, targeting: { ...f.targeting, instagram_positions: next } }));
  };

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        title="Meta Ads"
        subtitle="Gestão de campanhas Facebook & Instagram"
        icon={<FacebookIcon sx={{ color: '#1877F2' }} />}
        actions={
          <Stack direction="row" spacing={1} alignItems="center">
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <Select value={preset} onChange={e => setPreset(e.target.value)}>
                {PERIOD_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
              </Select>
            </FormControl>
            <Tooltip title="Recarregar"><IconButton onClick={load} disabled={loading}><RefreshIcon /></IconButton></Tooltip>
            <Tooltip title="Configuração"><IconButton onClick={() => setConfigOpen(true)}><SettingsIcon /></IconButton></Tooltip>
            <Button
              variant="contained"
              startIcon={<InstagramIcon />}
              onClick={openWizard}
              disabled={notConfigured || !config?.access_token_set}
              sx={{ background: 'linear-gradient(45deg,#f09433,#e6683c,#dc2743,#cc2366,#bc1888)', color: '#fff' }}
            >
              Criar para Instagram
            </Button>
          </Stack>
        }
      />

      {notConfigured && (
        <Alert severity="warning" sx={{ mb: 2 }} action={<Button size="small" onClick={() => setConfigOpen(true)}>Configurar</Button>}>
          Meta Ads não está configurado. Adicione as credenciais para activar a integração.
        </Alert>
      )}

      {summary && !loading && (
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={6} sm={3}><StatCard label="Impressões" value={fmt(summary.impressions)} icon={<Visibility />} color="primary" /></Grid>
          <Grid item xs={6} sm={3}><StatCard label="Alcance" value={fmt(summary.reach)} icon={<People />} color="success" /></Grid>
          <Grid item xs={6} sm={3}><StatCard label="Cliques" value={fmt(summary.clicks)} icon={<AdsClick />} color="error" /></Grid>
          <Grid item xs={6} sm={3}><StatCard label="Gasto" value={fmtMoney(summary.spend)} icon={<AttachMoney />} color="warning" /></Grid>
        </Grid>
      )}

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="Campanhas" />
        <Tab label={selectedCampaign ? `Ad Sets · ${selectedCampaign.name}` : 'Ad Sets'} disabled={!selectedCampaign} />
        <Tab label={selectedAdSet ? `Anúncios · ${selectedAdSet.name}` : 'Anúncios'} disabled={!selectedAdSet} />
        <Tab label="Conversions API" />
      </Tabs>

      {/* ── Tab 3: Conversions API ── */}
      {tab === 3 && <CapiPanel onNotify={showSnack} />}

      {/* ── Tab 0: Campanhas ── */}
      {tab === 0 && (
        loading ? <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box> :
        campaigns.length === 0 ? <EmptyState title="Sem campanhas" description="Crie a primeira campanha com o botão 'Criar para Instagram'." /> :
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Nome</TableCell>
                <TableCell>Objectivo</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Impressões</TableCell>
                <TableCell align="right">Cliques</TableCell>
                <TableCell align="right">Gasto</TableCell>
                <TableCell align="center">Acções</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {campaigns.map(c => (
                <TableRow key={c.id} hover sx={{ cursor: 'pointer' }}>
                  <TableCell onClick={() => handleSelectCampaign(c)}>
                    <Typography variant="body2" fontWeight={500}>{c.name}</Typography>
                    <Typography variant="caption" color="text.secondary">{new Date(c.created_time).toLocaleDateString('pt-CV', { timeZone: 'Atlantic/Cape_Verde' })}</Typography>
                  </TableCell>
                  <TableCell onClick={() => handleSelectCampaign(c)}>
                    <Typography variant="body2">{OBJECTIVES.find(o => o.value === c.objective)?.label ?? c.objective}</Typography>
                  </TableCell>
                  <TableCell><Chip label={c.status} size="small" color={STATUS_COLORS[c.status] ?? 'default'} /></TableCell>
                  <TableCell align="right">{fmt(c.insights?.impressions ?? 0)}</TableCell>
                  <TableCell align="right">{fmt(c.insights?.clicks ?? 0)}</TableCell>
                  <TableCell align="right">{c.insights ? fmtMoney(c.insights.spend) : '—'}</TableCell>
                  <TableCell align="center">
                    <Stack direction="row" spacing={0.5} justifyContent="center">
                      <Tooltip title="Ver Ad Sets"><IconButton size="small" onClick={() => handleSelectCampaign(c)}><PlayIcon fontSize="small" /></IconButton></Tooltip>
                      {(c.status === 'ACTIVE' || c.status === 'PAUSED') && (
                        <Tooltip title={c.status === 'ACTIVE' ? 'Pausar' : 'Activar'}>
                          <IconButton size="small" onClick={() => toggleCampaign(c)}>
                            {c.status === 'ACTIVE' ? <PauseIcon fontSize="small" /> : <PlayIcon fontSize="small" />}
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Eliminar"><IconButton size="small" color="error" onClick={() => deleteCampaign(c.id)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* ── Tab 1: Ad Sets ── */}
      {tab === 1 && (
        <Box>
          <Button startIcon={<BackIcon />} onClick={() => setTab(0)} sx={{ mb: 2 }}>Voltar às Campanhas</Button>
          {adSetsLoading ? <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box> :
          adSets.length === 0 ? <EmptyState title="Sem Ad Sets" description="Esta campanha ainda não tem conjuntos de anúncios. Use o wizard 'Criar para Instagram'." /> :
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Nome</TableCell>
                  <TableCell>Optimização</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Impressões</TableCell>
                  <TableCell align="right">Cliques</TableCell>
                  <TableCell align="right">Gasto</TableCell>
                  <TableCell align="center">Acções</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {adSets.map(as => (
                  <TableRow key={as.id} hover sx={{ cursor: 'pointer' }}>
                    <TableCell onClick={() => handleSelectAdSet(as)}>
                      <Typography variant="body2" fontWeight={500}>{as.name}</Typography>
                    </TableCell>
                    <TableCell><Typography variant="body2">{as.optimization_goal}</Typography></TableCell>
                    <TableCell><Chip label={as.status} size="small" color={STATUS_COLORS[as.status] ?? 'default'} /></TableCell>
                    <TableCell align="right">{fmt(as.insights?.impressions ?? 0)}</TableCell>
                    <TableCell align="right">{fmt(as.insights?.clicks ?? 0)}</TableCell>
                    <TableCell align="right">{as.insights ? fmtMoney(as.insights.spend) : '—'}</TableCell>
                    <TableCell align="center">
                      <Stack direction="row" spacing={0.5} justifyContent="center">
                        <Tooltip title="Ver Anúncios"><IconButton size="small" onClick={() => handleSelectAdSet(as)}><PlayIcon fontSize="small" /></IconButton></Tooltip>
                        {(as.status === 'ACTIVE' || as.status === 'PAUSED') && (
                          <Tooltip title={as.status === 'ACTIVE' ? 'Pausar' : 'Activar'}>
                            <IconButton size="small" onClick={() => toggleAdSet(as)}>
                              {as.status === 'ACTIVE' ? <PauseIcon fontSize="small" /> : <PlayIcon fontSize="small" />}
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Eliminar"><IconButton size="small" color="error" onClick={() => deleteAdSet(as.id)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>}
        </Box>
      )}

      {/* ── Tab 2: Anúncios ── */}
      {tab === 2 && (
        <Box>
          <Button startIcon={<BackIcon />} onClick={() => setTab(1)} sx={{ mb: 2 }}>Voltar aos Ad Sets</Button>
          {adsLoading ? <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box> :
          ads.length === 0 ? <EmptyState title="Sem anúncios" description="Este Ad Set ainda não tem anúncios." /> :
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Nome</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Impressões</TableCell>
                  <TableCell align="right">Cliques</TableCell>
                  <TableCell align="right">Gasto</TableCell>
                  <TableCell align="center">Acções</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {ads.map(ad => (
                  <TableRow key={ad.id} hover>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        {ad.creative?.thumbnail_url && (
                          <Box component="img" src={ad.creative.thumbnail_url} sx={{ width: 40, height: 40, borderRadius: 1, objectFit: 'cover' }} />
                        )}
                        <Typography variant="body2" fontWeight={500}>{ad.name}</Typography>
                      </Stack>
                    </TableCell>
                    <TableCell><Chip label={ad.status} size="small" color={STATUS_COLORS[ad.status] ?? 'default'} /></TableCell>
                    <TableCell align="right">{fmt(ad.insights?.impressions ?? 0)}</TableCell>
                    <TableCell align="right">{fmt(ad.insights?.clicks ?? 0)}</TableCell>
                    <TableCell align="right">{ad.insights ? fmtMoney(ad.insights.spend) : '—'}</TableCell>
                    <TableCell align="center">
                      <Stack direction="row" spacing={0.5} justifyContent="center">
                        {(ad.status === 'ACTIVE' || ad.status === 'PAUSED') && (
                          <Tooltip title={ad.status === 'ACTIVE' ? 'Pausar' : 'Activar'}>
                            <IconButton size="small" onClick={() => toggleAd(ad)}>
                              {ad.status === 'ACTIVE' ? <PauseIcon fontSize="small" /> : <PlayIcon fontSize="small" />}
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Eliminar"><IconButton size="small" color="error" onClick={() => deleteAd(ad.id)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>}
        </Box>
      )}

      {/* ── Wizard: Criar Campanha Instagram ── */}
      <Dialog open={wizardOpen} onClose={() => !wizardSaving && setWizardOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Stack direction="row" spacing={1} alignItems="center">
            <InstagramIcon sx={{ color: '#bc1888' }} />
            <span>Criar Campanha Instagram</span>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Stepper activeStep={wizardStep} sx={{ mb: 3 }}>
            {WIZARD_STEPS.map(label => <Step key={label}><StepLabel>{label}</StepLabel></Step>)}
          </Stepper>

          {/* Passo 0: Campanha */}
          {wizardStep === 0 && (
            <Stack spacing={2}>
              <TextField label="Nome da campanha" value={campaignForm.name}
                onChange={e => setCampaignForm(f => ({ ...f, name: e.target.value }))} fullWidth required />
              <FormControl fullWidth required>
                <InputLabel>Objectivo</InputLabel>
                <Select label="Objectivo" value={campaignForm.objective}
                  onChange={e => setCampaignForm(f => ({ ...f, objective: e.target.value as MetaCampaignObjective }))}>
                  {OBJECTIVES.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                </Select>
              </FormControl>
              <TextField label="Orçamento diário (CVE)" type="number" value={campaignForm.daily_budget}
                onChange={e => setCampaignForm(f => ({ ...f, daily_budget: Number(e.target.value) }))}
                InputProps={{ startAdornment: <InputAdornment position="start">CVE</InputAdornment> }}
                fullWidth required helperText="Orçamento total da campanha por dia" />
              <FormControlLabel
                control={<Switch checked={campaignForm.status === 'ACTIVE'}
                  onChange={e => setCampaignForm(f => ({ ...f, status: e.target.checked ? 'ACTIVE' : 'PAUSED' }))} />}
                label="Activar imediatamente"
              />
            </Stack>
          )}

          {/* Passo 1: Ad Set */}
          {wizardStep === 1 && (
            <Stack spacing={2}>
              <Alert severity="info">Define onde e para quem o anúncio vai aparecer no Instagram.</Alert>
              <TextField label="Nome do Ad Set" value={adSetForm.name}
                onChange={e => setAdSetForm(f => ({ ...f, name: e.target.value }))} fullWidth required />
              <TextField label="Orçamento diário deste Ad Set (CVE)" type="number" value={adSetForm.daily_budget}
                onChange={e => setAdSetForm(f => ({ ...f, daily_budget: Number(e.target.value) }))}
                InputProps={{ startAdornment: <InputAdornment position="start">CVE</InputAdornment> }}
                disabled
                fullWidth
                helperText="O orçamento fica na campanha (optimização de orçamento do Meta). Um ad set com orçamento próprio seria rejeitado." />
              <Box>
                <Typography variant="body2" fontWeight={500} gutterBottom>Posicionamentos Instagram</Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                  {IG_POSITIONS.map(p => (
                    <Chip
                      key={p.value}
                      label={p.label}
                      clickable
                      color={(adSetForm.targeting.instagram_positions || []).includes(p.value) ? 'primary' : 'default'}
                      onClick={() => toggleIgPosition(p.value)}
                    />
                  ))}
                </Stack>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField label="Idade mínima" type="number" value={adSetForm.targeting.age_min ?? 18}
                    onChange={e => setAdSetForm(f => ({ ...f, targeting: { ...f.targeting, age_min: Number(e.target.value) } }))}
                    fullWidth inputProps={{ min: 13, max: 65 }} />
                </Grid>
                <Grid item xs={6}>
                  <TextField label="Idade máxima" type="number" value={adSetForm.targeting.age_max ?? 65}
                    onChange={e => setAdSetForm(f => ({ ...f, targeting: { ...f.targeting, age_max: Number(e.target.value) } }))}
                    fullWidth inputProps={{ min: 13, max: 65 }} />
                </Grid>
              </Grid>
            </Stack>
          )}

          {/* Passo 2: Anúncio */}
          {wizardStep === 2 && (
            <Stack spacing={2}>
              <Alert severity="info">Define o criativo — o que o utilizador vai ver no Instagram.</Alert>
              <TextField label="Nome do anúncio" value={adForm.name}
                onChange={e => setAdForm(f => ({ ...f, name: e.target.value }))} fullWidth required />
              <TextField label="Texto do anúncio (caption)" multiline rows={3} value={adForm.message}
                onChange={e => setAdForm(f => ({ ...f, message: e.target.value }))} fullWidth required
                helperText="Texto que aparece no feed/story do Instagram" />
              <TextField label="Título / Headline" value={adForm.headline}
                onChange={e => setAdForm(f => ({ ...f, headline: e.target.value }))} fullWidth />
              <TextField label="URL de destino" value={adForm.link}
                onChange={e => setAdForm(f => ({ ...f, link: e.target.value }))} fullWidth required
                helperText="Para onde o utilizador vai ao clicar" />
              <TextField label="URL da imagem (opcional)" value={adForm.image_url}
                onChange={e => setAdForm(f => ({ ...f, image_url: e.target.value }))} fullWidth
                helperText="Link directo para a imagem do anúncio (JPEG/PNG)" />
              <FormControl fullWidth>
                <InputLabel>Call to Action</InputLabel>
                <Select label="Call to Action" value={adForm.call_to_action}
                  onChange={e => setAdForm(f => ({ ...f, call_to_action: e.target.value }))}>
                  {CTA_OPTIONS.map(c => <MenuItem key={c} value={c}>{c.replace(/_/g, ' ')}</MenuItem>)}
                </Select>
              </FormControl>
              <FormControlLabel
                control={<Switch checked={adForm.status === 'ACTIVE'}
                  onChange={e => setAdForm(f => ({ ...f, status: e.target.checked ? 'ACTIVE' : 'PAUSED' }))} />}
                label="Activar anúncio imediatamente"
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWizardOpen(false)} disabled={wizardSaving}>Cancelar</Button>
          {wizardStep > 0 && <Button onClick={handleWizardBack} disabled={wizardSaving} startIcon={<BackIcon />}>Anterior</Button>}
          <Button variant="contained" onClick={handleWizardNext} disabled={wizardSaving}
            sx={wizardStep === 2 ? { background: 'linear-gradient(45deg,#f09433,#e6683c,#dc2743,#cc2366,#bc1888)' } : {}}>
            {wizardSaving ? <CircularProgress size={18} /> : wizardStep === 2 ? 'Publicar' : 'Seguinte'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Config Dialog ── */}
      <Dialog open={configOpen} onClose={() => setConfigOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Configuração Meta Ads</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Alert severity="info">O access token não é mostrado por segurança. Deixe vazio para manter o actual.</Alert>
            <TextField label="Access Token" type="password" value={configForm.access_token}
              onChange={e => setConfigForm(f => ({ ...f, access_token: e.target.value }))} fullWidth
              helperText={config?.access_token_set ? 'Token já configurado' : 'Token não configurado'} />
            <TextField label="Ad Account ID" value={configForm.ad_account_id}
              onChange={e => setConfigForm(f => ({ ...f, ad_account_id: e.target.value }))} fullWidth helperText="Formato: act_XXXXXXXXX" />
            <TextField label="Page ID" value={configForm.page_id}
              onChange={e => setConfigForm(f => ({ ...f, page_id: e.target.value }))} fullWidth />
            <TextField label="Instagram User ID" value={configForm.ig_user_id}
              onChange={e => setConfigForm(f => ({ ...f, ig_user_id: e.target.value }))} fullWidth />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfigOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleSaveConfig} disabled={configSaving}>
            {configSaving ? <CircularProgress size={18} /> : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snack.open} autoHideDuration={4000} onClose={() => setSnack(s => ({ ...s, open: false }))}>
        <Alert severity={snack.severity} onClose={() => setSnack(s => ({ ...s, open: false }))}>{snack.message}</Alert>
      </Snackbar>
    </Box>
  );
}
